/**
 * Changes this browser just saved on a client, shown in the portal client
 * lists straight away. The lists read server-written rows
 * (src/services/liveClientsStore.js), which follow a save by about a second,
 * so without these an archive, DNQ, release, new client or delete would
 * appear late, or flip back while the server catches up.
 *
 * A patch is dropped once a delivered row shows it, or after LOCAL_PATCH_MS.
 * No Firebase imports: clientStateService records every save here.
 */
import { isClientListRow, markClientListRow } from '../utils/clientListRow';

const LOCAL_PATCH_MS = 15 * 1000;

// clientId → { fields: { dotPath: primitive } | null, client: object | null, removed: boolean, expiresAt }
const patches = new Map();
let onPatchesChanged = null;

function readPath(obj, path) {
  return path.split('.').reduce((node, key) => (node && typeof node === 'object' ? node[key] : undefined), obj);
}

function withFields(client, fields) {
  const copy = { ...client };
  if (isClientListRow(client)) markClientListRow(copy);
  Object.entries(fields).forEach(([path, value]) => {
    const keys = path.split('.');
    let node = copy;
    keys.slice(0, -1).forEach((key) => {
      node[key] = node[key] && typeof node[key] === 'object' ? { ...node[key] } : {};
      node = node[key];
    });
    node[keys[keys.length - 1]] = value;
  });
  return copy;
}

function setPatch(clientId, patch) {
  if (!clientId) return;
  patches.set(clientId, { fields: null, client: null, removed: false, ...patch, expiresAt: Date.now() + LOCAL_PATCH_MS });
  if (onPatchesChanged) onPatchesChanged();
}

/** The store re-publishes its list when a patch is added or dropped. */
export function setLiveClientPatchListener(listener) {
  onPatchesChanged = listener;
}

/**
 * Show fields just saved on a client. Only plain values on dot paths are
 * applied (`{ isArchived: true, 'assignment.assignedAdviser': … }`); maps and
 * Firestore field values are left for the row.
 */
export function patchLiveClient(clientId, updates) {
  const fields = {};
  Object.entries(updates || {}).forEach(([path, value]) => {
    if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) fields[path] = value;
  });
  if (Object.keys(fields).length === 0) return;
  const previous = patches.get(clientId);
  setPatch(clientId, {
    client: previous?.client || null,
    fields: { ...(previous?.fields || {}), ...fields },
  });
}

/** A client this browser just created: listed until its row arrives. */
export function addLiveClient(client) {
  if (!client?.id) return;
  setPatch(client.id, { client });
}

/** A client this browser just deleted: hidden until its row is gone. */
export function removeLiveClient(clientId) {
  setPatch(clientId, { removed: true });
}

/** Undo a patch (the save it showed failed). */
export function dropLiveClientPatch(clientId) {
  if (patches.delete(clientId) && onPatchesChanged) onPatchesChanged();
}

export function clearLiveClientPatches() {
  patches.clear();
}

/** A row was delivered (or removed, `row` null): drop the patch it now shows. */
export function settleLiveClientPatch(clientId, row) {
  const patch = patches.get(clientId);
  if (!patch) return;
  if (!row) {
    if (patch.removed) patches.delete(clientId);
    return;
  }
  if (patch.removed) return;
  const fieldsMatch = !patch.fields
    || Object.entries(patch.fields).every(([path, value]) => readPath(row, path) === value);
  if (fieldsMatch) patches.delete(clientId);
}

/** `byId` (client id → client) with the live patches applied, in place. */
export function applyLiveClientPatches(byId) {
  const now = Date.now();
  patches.forEach((patch, id) => {
    if (patch.expiresAt <= now) {
      patches.delete(id);
      return;
    }
    if (patch.removed) {
      byId.delete(id);
      return;
    }
    const base = byId.get(id) || patch.client;
    if (base) byId.set(id, patch.fields ? withFields(base, patch.fields) : base);
  });
  return byId;
}
